import { Fragment, useMemo, useState } from "react";

import { useCatalogueDrafts } from "@/api/catalogue";
import { EntryDetail } from "@/components/catalogue/EntryDetail";
import { viewFromDraftRow } from "@/components/catalogue/entryDetailAdapter";
import { OwningJobCell } from "@/components/catalogue/OwningJobCell";
import {
  ariaSortFor,
  defaultSortDir,
  SortableHeader,
  sortRows,
  useCycleSort,
  useExpandedIds,
} from "@/components/catalogue/sort";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { ExpandToggleButton } from "./Catalogue";

/**
 * `/catalogue/drafts` — every row in `tools_draft`, regardless of
 * owning job's state. Distinct from `/review` (which only shows
 * `pending_review` batches): drafts attached to `committing` /
 * `completed` / `expired` / orphaned jobs are visible here.
 *
 * Each row links to its owning job when discoverable
 * (`validator_result.scoredRows` holds the draft id). Otherwise the row
 * is "orphaned" — most often a curator job that errored before the
 * validator hop, since the curator's artifact doesn't carry per-row
 * draft IDs.
 *
 * Search + sort + expand-on-click parity with `/catalogue` lands per
 * issue #218 — operators wanted the same affordances on the in-flight
 * surface they get on the promoted catalogue.
 */

const COLUMN_COUNT = 8;

export function CatalogueDrafts() {
  const query = useCatalogueDrafts();
  const [search, setSearch] = useState<string>("");
  const { sortKey, sortDir, cycle: cycleSort } = useCycleSort(
    "last_updated",
    "desc",
    defaultSortDir,
  );
  const { expandedIds, toggle: toggleExpanded } = useExpandedIds();

  const filteredSorted = useMemo(() => {
    if (!query.data) return [];
    const needle = search.trim().toLowerCase();
    const matched = query.data.rows.filter((row) => {
      if (needle.length === 0) return true;
      return String(row.name ?? "").toLowerCase().includes(needle);
    });
    return sortRows(matched, sortKey, sortDir);
  }, [query.data, search, sortKey, sortDir]);

  if (query.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  const rows = query.data.rows;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No drafts in flight"
        description="tools_draft is empty. Curator runs land here as draft batches; they clear after HITL rollup."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-text-muted">
          {rows.length} draft{rows.length === 1 ? "" : "s"} in flight
        </p>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="w-[220px]"
          aria-label="Search by name"
        />
      </div>

      {filteredSorted.length === 0 ? (
        <EmptyState
          title="No drafts match"
          description="Adjust the search to widen the result set."
        />
      ) : (
        <div className="border border-border rounded bg-bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>ID</TableHead>
                <TableHead aria-sort={ariaSortFor(sortKey === "name", sortDir)}>
                  <SortableHeader
                    label="Name"
                    active={sortKey === "name"}
                    dir={sortDir}
                    onClick={() => cycleSort("name")}
                  />
                </TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Quality</TableHead>
                <TableHead>Flag</TableHead>
                <TableHead>Owning job</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSorted.map((row) => {
                const id = String(row.id);
                const expanded = expandedIds.has(id);
                return (
                  <Fragment key={id}>
                    <TableRow data-testid={`drafts-row-${id}`}>
                      <TableCell className="w-8 p-0">
                        <ExpandToggleButton
                          expanded={expanded}
                          onToggle={() => toggleExpanded(id)}
                          rowLabel={String(row.name ?? id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-accent">
                        {id}
                      </TableCell>
                      <TableCell>{String(row.name ?? "")}</TableCell>
                      <TableCell className="text-sm text-text-muted">
                        {String(row.vendor ?? "")}
                      </TableCell>
                      <TableCell className="text-sm">{String(row.category ?? "")}</TableCell>
                      <TableCell className="text-sm">
                        {formatQuality(row.quality_score)}
                      </TableCell>
                      <TableCell>
                        <FlagCell value={row.validator_flag} />
                      </TableCell>
                      <TableCell className="text-xs">
                        <OwningJobCell jobId={row.jobId} jobNumber={row.jobNumber} />
                      </TableCell>
                    </TableRow>
                    {expanded ? (
                      <TableRow data-testid={`drafts-row-${id}-expanded`}>
                        <TableCell
                          colSpan={COLUMN_COUNT}
                          className="bg-bg/40 p-4"
                        >
                          <EntryDetail view={viewFromDraftRow(row)} raw={row} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * Header-keyed cells on `CatalogueDraftRow` are read via the row's
 * string index signature. Under `noUncheckedIndexedAccess` that returns
 * `T | undefined`, so the cell helpers below take the widened type
 * directly rather than a typed indexed access.
 */
type CellValue = string | number | boolean | null | undefined;

function FlagCell({ value }: { value: CellValue }) {
  // `validator_flag` arrives from Sheets as either a boolean or a
  // string ("TRUE"/"FALSE"). The curator/validator write strings;
  // be defensive on both.
  const flagged =
    value === true ||
    (typeof value === "string" && value.trim().toUpperCase() === "TRUE");
  if (flagged) return <Badge variant="danger">flagged</Badge>;
  return <span className="text-text-faint">—</span>;
}

function formatQuality(value: CellValue): string {
  if (typeof value === "number") return value.toFixed(2);
  if (typeof value === "string" && value.length > 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : value;
  }
  return "—";
}
